import { FC, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-use';

import Flex from 'quarks/Flex';
import Link from 'quarks/Link';
import Text from 'quarks/Text';
import type { BasicProps } from 'quarks/interpolations/basic';

import { stringToKebabCase } from 'utils/functions';

import type {
  ContentfulComponentSingleInstanceTableOfContentsJsonNode,
  ContentfulTemplateBlogTableOfContentsJsonNode,
  ContentfulTemplateCaseStudyTableOfContentsJsonNode,
  ContentfulTemplateEguidesTableOfContentsJsonNode,
  ContentfulTemplateResourceTableOfContentsJsonNode,
  ContentfulTemplateTipsAndToolsTableOfContentsJsonNode,
} from 'graphqlTypes';

export type TableOfContentProps = BasicProps & {
  heading?: string;
  tableOfContents:
    | ContentfulTemplateCaseStudyTableOfContentsJsonNode[]
    | ContentfulTemplateBlogTableOfContentsJsonNode[]
    | ContentfulTemplateTipsAndToolsTableOfContentsJsonNode[]
    | ContentfulTemplateResourceTableOfContentsJsonNode[]
    | ContentfulComponentSingleInstanceTableOfContentsJsonNode[]
    | ContentfulTemplateEguidesTableOfContentsJsonNode[];
  hasForm?: boolean;
  hasSocial?: boolean;
};

const TableOfContents: FC<TableOfContentProps> = ({ heading, tableOfContents, hasForm, hasSocial }) => {
  const { pathname } = useLocation();
  const [activeIndex, setActiveIndex] = useState(0);
  const linkContainerRef = useRef<null | HTMLDivElement>(null);
  /* eslint-disable react-hooks/rules-of-hooks */
  const linkRefs = tableOfContents?.map(() => useRef<null | HTMLElement>());
  const numberOfLinks = tableOfContents?.length || 0;
  const min = 4;
  const max = 7;
  const lastWheelEventRef = useRef(null);

  useEffect(() => {
    if (linkContainerRef?.current) {
      linkContainerRef.current.onwheel = e => e.preventDefault();
    }
  }, [linkContainerRef]);

  useEffect(() => {
    if (numberOfLinks > max - 1) {
      let top = 0;
      if (activeIndex < min) {
        top = 0;
      } else {
        top = linkRefs[activeIndex]?.current?.offsetTop - linkContainerRef?.current?.offsetTop - 60;
      }
      if (linkContainerRef?.current?.scrollTo instanceof Function) {
        linkContainerRef.current.scrollTo({ top });
      }
    }
  }, [activeIndex]);

  const handleWheel = () => {
    lastWheelEventRef.current = new Date().getTime();
  };

  const handleScroll = () => {
    const currentTime = new Date().getTime();

    // Assume that if a wheel event happened recently, the scroll was caused by the wheel
    if (lastWheelEventRef.current && currentTime - lastWheelEventRef.current < 200) {
      const topOffset = 96 + 30 + 30; // max nav height + scroll offset + offset
      const offsetTopArray = tableOfContents?.map(item => {
        const content = item?.content?.[0];

        if (content) {
          const elementId = stringToKebabCase(content?.value || '') || '';
          const eleHeading = document.getElementById(elementId);

          if (eleHeading?.nodeType === 1) {
            const rect = eleHeading.getBoundingClientRect();

            return rect.top;
          }
        }

        return null;
      });

      offsetTopArray.forEach((offset, index) => {
        if (!offset) {
          return;
        }
        if (offset < topOffset && offsetTopArray?.[index + 1] > topOffset) {
          setActiveIndex(index);
        }
      });
    }
  };

  useEffect(() => {
    window.addEventListener('scroll', handleScroll);
    window.addEventListener('wheel', handleWheel);

    return () => {
      window.removeEventListener('scroll', handleScroll);
      window.addEventListener('wheel', handleWheel);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <Flex data-testid="tocContainer" flexDirection="column" gap="4px" width="100%">
      <Flex justifyContent="space-between" width="100%" alignItems="center" marginBottom={16}>
        <Text textColor="gray-900" textStyle="textMd" fontWeight="semiBold">
          {heading}
        </Text>
      </Flex>
      <Flex
        ref={linkContainerRef}
        flexDirection="column"
        borderRadius="12px"
        border="1px solid"
        borderColor="gray-200"
        gap="12px"
        paddingAll={24}
        overflowY="auto"
        maxHeight={`calc(100vh - ${hasSocial ? 300 : 0}px - ${hasForm ? 300 : 0}px)`}
      >
        {tableOfContents?.map((item, index) => {
          const content = item?.content && item.content[0];
          const isVisible = content && content.isVisible;
          const id = stringToKebabCase(content?.value?.toString() || '');

          return (
            isVisible && (
              <Link
                ref={linkRefs[index]}
                key={content?.value}
                href={`${pathname}#${id}`}
                paddingLeft={8}
                textStyle="textXs"
                textDecoration="none"
                textColor="gray-900"
                borderLeft="3px solid"
                borderColor={activeIndex === index ? 'green-300' : 'common-transparent'}
                transition="200ms ease-in-out"
                hover={{ textColor: 'gray-700' }}
                onClick={() => setActiveIndex(index)}
              >
                {content?.value}
              </Link>
            )
          );
        })}
      </Flex>
    </Flex>
  );
};

export default TableOfContents;

TableOfContents.defaultProps = {
  heading: 'In this article',
};
